import React, { useMemo, useState } from "react";
import CustomToolbar from "../global/CustomToolbar";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import ArchiveIcon from "@material-ui/icons/Archive";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ConfirmDialog from "./ConfirmDialog";
import { MONTHS_SHORT } from "./utils";

function PTToolbar({ title, onBack, actions }) {
  const headerButtons = [];
  if (onBack) {
    headerButtons.push({ icon: <KeyboardBackspaceIcon />, title: "Back", handler: onBack });
  }
  (actions || []).forEach((a) => headerButtons.push(a));
  return <CustomToolbar title={title} headerButtons={headerButtons} />;
}

const P_COLORS = [
  "#2563eb", "#059669", "#d97706", "#7c3aed", "#ea580c",
  "#dc2626", "#0891b2", "#be185d", "#4f46e5", "#0d9488",
  "#65a30d", "#6366f1", "#f59e0b",
];
const colorFor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return P_COLORS[h % P_COLORS.length];
};

const codeFor = (name) =>
  (name || "").replace(/[^A-Z0-9]/gi, "").slice(0, 3).toUpperCase() || "LOC";

/* ═══════════════════════════════════════════════════════════
   Settings landing
   ══════════════════════════════════════════════════════════ */

export function SettingsMain({ nav, locations }) {
  const activeLocations = locations.filter((l) => !l.archived);
  const partnerSet = new Set();
  activeLocations.forEach((l) => (l.investors || []).forEach((i) => partnerSet.add(i.name)));
  const activeCount = activeLocations.length;
  const archivedCount = locations.length - activeCount;

  return (
    <>
      <PTToolbar title="Settings" onBack={() => nav("landing")} />
      <div className="pt-content">
        <div className="pt-lb">Configuration</div>
        <button className="pt-listbtn" onClick={() => nav("sLocs")}>
          <div>
            <div className="name"><span role="img" aria-label="location pin">📍</span> Locations</div>
            <div className="meta">
              {activeCount} active{archivedCount > 0 ? ` · ${archivedCount} archived` : ""}
            </div>
          </div>
          <span style={{ color: "var(--pt-muted)" }}>›</span>
        </button>
        <button className="pt-listbtn" onClick={() => nav("sPartners")}>
          <div>
            <div className="name"><span role="img" aria-label="people">👥</span> Partners</div>
            <div className="meta">{partnerSet.size} across all locations</div>
          </div>
          <span style={{ color: "var(--pt-muted)" }}>›</span>
        </button>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Locations list — inline creation + cards that open detail
   ══════════════════════════════════════════════════════════ */

export function LocsList({ nav, locations, setLocations, api, isAdmin }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const activeLocations = locations.filter((l) => !l.archived);
  const archivedLocations = locations.filter((l) => l.archived);

  // Card renderer shared by the active list and the (collapsible) archived list.
  const renderLocationCard = (loc, isArchived = false) => {
    const code = codeFor(loc.name);
    const owners = (loc.investors || []).filter((i) => !i.isManager);
    const pct = owners.reduce((s, i) => s + (Number(i.percentage) || 0), 0);
    const ok = owners.length > 0 && Math.abs(pct - 100) < 0.01;
    return (
      <button
        key={loc.locationId}
        className="pt-listbtn"
        onClick={() => nav("sLocDetail", { loc: loc.locationId })}
        style={{ alignItems: "flex-start", opacity: isArchived ? 0.7 : 1 }}
      >
        <div style={{ display: "flex", gap: 12, flex: 1 }}>
          <div
            className={`pt-loc-badge ${isArchived ? "" : "active"}`}
            style={{ width: 42, height: 42, flexShrink: 0 }}
          >
            {code}
          </div>
          <div style={{ textAlign: "left", flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span className="name" style={{ fontSize: 15 }}>{loc.name}</span>
              {isArchived && <span style={archivedBadge}>Archived</span>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {owners.length === 0 ? (
                <span style={{ color: "var(--pt-amber-txt)", fontSize: 11, fontWeight: 500 }}>
                  No partners assigned
                </span>
              ) : (
                owners.map((inv) => (
                  <span key={inv.investorId} style={partnerPill(colorFor(inv.name))}>
                    {inv.name} {Number(inv.percentage) || 0}%
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {owners.length > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 700,
              color: ok ? "var(--pt-green)" : "var(--pt-red)",
              background: ok ? "#ecfdf5" : "#fef2f2",
              border: `1px solid ${ok ? "#a7f3d0" : "#fecaca"}`,
              padding: "3px 8px", borderRadius: 10,
            }}>
              {pct}%
            </span>
          )}
          <span style={{ color: "var(--pt-muted)", fontSize: 16 }}>›</span>
        </div>
      </button>
    );
  };

  const createLocation = () => {
    const name = newName.trim();
    if (!name) {
      setError("Enter a name first.");
      return;
    }
    setSaving(true);
    setError(null);
    api.saveLocation(
      { name },
      (res) => {
        setSaving(false);
        if (!res?.success || !res?.location) {
          setError(res?.message || "Failed to create location.");
          return;
        }
        const newLoc = { investors: [], ...res.location };
        setLocations((prev) => [...prev, newLoc]);
        setCreating(false);
        setNewName("");
        nav("sLocDetail", { loc: newLoc.locationId });
      },
      (err) => {
        setSaving(false);
        setError(typeof err === "string" ? err : "Failed to create location.");
      },
    );
  };

  // + New is admin-only
  const headerActions = isAdmin
    ? [
        {
          icon: <AddCircleIcon />,
          title: creating ? "Cancel" : "New location",
          handler: () => {
            setCreating((v) => !v);
            setError(null);
          },
        },
      ]
    : [];

  return (
    <>
      <PTToolbar
        title="Locations"
        onBack={() => nav("settings")}
        actions={headerActions}
      />
      <div className="pt-content">
        {creating && (
          <div className="pt-card" style={{ borderColor: "var(--pt-teal)", boxShadow: "var(--pt-shadow-md)" }}>
            <div className="pt-lb" style={{ color: "var(--pt-teal)" }}>New location</div>
            <input
              autoFocus
              type="text"
              placeholder="Location name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createLocation()}
              style={inputStyle}
            />
            {error && <div className="pt-error" style={{ marginTop: 8 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                onClick={createLocation}
                disabled={saving || !newName.trim()}
                style={primaryBtnStyle(!saving && !!newName.trim())}
              >
                {saving ? "Creating…" : "Create & assign partners"}
              </button>
              <button
                onClick={() => { setCreating(false); setNewName(""); setError(null); }}
                style={ghostBtnStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {locations.length === 0 && !creating && (
          <div className="pt-empty">No locations yet. Tap the + icon to create one.</div>
        )}

        {activeLocations.map((loc) => renderLocationCard(loc))}

        {archivedLocations.length > 0 && (
          <>
            <button
              className="pt-lb"
              onClick={() => setShowArchived((v) => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 6, width: "100%",
                background: "none", border: "none", cursor: "pointer",
                color: "var(--pt-muted)", padding: "14px 0 6px", fontFamily: "inherit",
              }}
            >
              <ArchiveIcon style={{ fontSize: 16 }} />
              Archived ({archivedLocations.length})
              <span style={{ marginLeft: "auto", fontSize: 12 }}>{showArchived ? "▲" : "▼"}</span>
            </button>
            {showArchived && archivedLocations.map((loc) => renderLocationCard(loc, true))}
          </>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Location detail — partner management
   ══════════════════════════════════════════════════════════ */

export function LocDetail({ nav, locationId, locations, setLocations, api, isAdmin }) {
  const location = useMemo(
    () => locations.find((l) => l.locationId === locationId) || null,
    [locations, locationId],
  );
  const [error, setError] = useState(null);
  const [savingOp, setSavingOp] = useState(null); // id of row being saved / 'new' / 'rename' / 'delete'
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(location?.name || "");
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState("");
  const [newIsManager, setNewIsManager] = useState(false);
  const [newThreshold, setNewThreshold] = useState("");
  // dialog state — one at a time is enough for settings flows
  const [confirm, setConfirm] = useState(null); // { kind: 'archive'|'unarchive'|'delete'|'investor', target?: investor }

  if (!location) {
    return (
      <>
        <PTToolbar title="Location" onBack={() => nav("sLocs")} />
        <div className="pt-content">
          <div className="pt-empty">Location not found.</div>
        </div>
      </>
    );
  }

  const investors = location.investors || [];
  const owners = investors.filter((i) => !i.isManager);
  const ownerTotal = owners.reduce((s, i) => s + (Number(i.percentage) || 0), 0);
  const remaining = Math.max(0, 100 - ownerTotal);

  // Every partner name that already exists anywhere, excluding those already on this location
  const allNames = new Set();
  locations.forEach((l) => (l.investors || []).forEach((i) => allNames.add(i.name)));
  const usedHere = new Set(investors.map((i) => i.name));
  const availableNames = Array.from(allNames).filter((n) => !usedHere.has(n));

  // helpers — mutate lifted locations state after successful API calls
  const patchLocation = (patch) =>
    setLocations((prev) =>
      prev.map((l) => (l.locationId === locationId ? { ...l, ...patch } : l)),
    );
  const patchInvestors = (fn) => patchLocation({ investors: fn(investors) });

  const doRename = () => {
    const name = renameValue.trim();
    if (!name || name === location.name) { setRenameOpen(false); return; }
    setSavingOp("rename");
    setError(null);
    api.renameLocation(
      { locationId, name },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Rename failed"); return; }
        patchLocation({ name: res.location?.name || name });
        setRenameOpen(false);
      },
      (err) => { setSavingOp(null); setError(typeof err === "string" ? err : "Rename failed"); },
    );
  };

  const doArchiveLocation = () => {
    setSavingOp("archive");
    setError(null);
    api.archiveLocation(
      { locationId },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Archive failed"); setConfirm(null); return; }
        setConfirm(null);
        patchLocation({ archived: true, archivedAt: res.location?.archivedAt || null });
        nav("sLocs");
      },
      (err) => {
        setSavingOp(null);
        setConfirm(null);
        setError(typeof err === "string" ? err : "Archive failed");
      },
    );
  };

  const doUnarchiveLocation = () => {
    setSavingOp("unarchive");
    setError(null);
    api.unarchiveLocation(
      { locationId },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Unarchive failed"); setConfirm(null); return; }
        setConfirm(null);
        patchLocation({ archived: false, archivedAt: null });
      },
      (err) => {
        setSavingOp(null);
        setConfirm(null);
        setError(typeof err === "string" ? err : "Unarchive failed");
      },
    );
  };

  const doDeleteLocation = () => {
    setSavingOp("delete");
    setError(null);
    api.deleteLocation(
      { locationId },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Delete failed"); setConfirm(null); return; }
        setConfirm(null);
        setLocations((prev) => prev.filter((l) => l.locationId !== locationId));
        nav("sLocs");
      },
      (err) => {
        setSavingOp(null);
        setConfirm(null);
        setError(typeof err === "string" ? err : "Delete failed");
      },
    );
  };

  const addPartner = (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    const asManager = newIsManager;
    // managers are deducted BEFORE the owner split, so they don't take owner %.
    const pct = asManager ? 0 : remaining;
    const threshold = asManager ? Number(newThreshold) || 0 : 0;
    setSavingOp("new");
    setError(null);
    api.saveInvestor(
      {
        locationId,
        name: trimmed,
        percentage: pct,
        profitThreshold: threshold,
        isManager: asManager,
      },
      (res) => {
        setSavingOp(null);
        if (!res?.success || !res?.investor) { setError(res?.message || "Add failed"); return; }
        patchInvestors((list) => [...list, res.investor]);
        setNewPartnerName("");
        setNewIsManager(false);
        setNewThreshold("");
        setShowAddPanel(false);
      },
      (err) => { setSavingOp(null); setError(typeof err === "string" ? err : "Add failed"); },
    );
  };

  // Apply any subset of { percentage, profitThreshold, isManager } to an existing investor.
  const applyUpdate = (inv, patch) => {
    const merged = {
      investorId: inv.investorId,
      name: patch.name !== undefined ? String(patch.name).trim() : inv.name,
      percentage: patch.percentage !== undefined ? Number(patch.percentage) || 0 : Number(inv.percentage) || 0,
      profitThreshold: patch.profitThreshold !== undefined ? Number(patch.profitThreshold) || 0 : Number(inv.profitThreshold) || 0,
      isManager: patch.isManager !== undefined ? !!patch.isManager : !!inv.isManager,
    };
    // When flipping to manager, drop the owner percentage to 0; when flipping back, leave pct to the user.
    if (patch.isManager === true) merged.percentage = 0;
    setSavingOp(inv.investorId);
    setError(null);
    api.updateInvestor(
      merged,
      (res) => {
        setSavingOp(null);
        if (!res?.success || !res?.investor) { setError(res?.message || "Update failed"); return; }
        patchInvestors((list) =>
          list.map((i) => (i.investorId === inv.investorId ? res.investor : i)),
        );
      },
      (err) => { setSavingOp(null); setError(typeof err === "string" ? err : "Update failed"); },
    );
  };

  const removePartner = (inv) => {
    setSavingOp(inv.investorId);
    setError(null);
    api.deleteInvestor(
      { investorId: inv.investorId },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Delete failed"); setConfirm(null); return; }
        setConfirm(null);
        patchInvestors((list) => list.filter((i) => i.investorId !== inv.investorId));
      },
      (err) => {
        setSavingOp(null);
        setConfirm(null);
        setError(typeof err === "string" ? err : "Delete failed");
      },
    );
  };

  const totalOk = Math.abs(ownerTotal - 100) < 0.01;

  // Archive/unarchive + edit actions are admin-only. Non-admins see the screen
  // read-only. Archive is the primary, reversible action; permanent delete is a
  // separate low-emphasis button lower on the screen.
  const headerActions = isAdmin
    ? [
        location.archived
          ? {
              icon: <UnarchiveIcon />,
              title: "Unarchive location",
              handler: () => setConfirm({ kind: "unarchive" }),
              disabled: savingOp === "unarchive",
            }
          : {
              icon: <ArchiveIcon />,
              title: "Archive location",
              handler: () => setConfirm({ kind: "archive" }),
              disabled: savingOp === "archive",
            },
      ]
    : [];

  return (
    <>
      <PTToolbar
        title={location.name}
        onBack={() => nav("sLocs")}
        actions={headerActions}
      />
      <div className="pt-content">
        {error && <div className="pt-error">{error}</div>}

        {/* Header card */}
        <div
          className={`pt-card pt-loc-header ${renameOpen ? "renaming" : ""}`}
          style={{ display: "flex", alignItems: "center", gap: 14 }}
        >
          <div className="pt-loc-badge active" style={{ width: 52, height: 52, fontSize: 13 }}>
            {codeFor(location.name)}
          </div>
          <div className="pt-loc-name-wrap" style={{ flex: 1, minWidth: 0 }}>
            {!renameOpen ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 20, fontWeight: 700, color: "var(--pt-text)" }}>{location.name}</span>
                  {location.archived && <span style={archivedBadge}>Archived</span>}
                </div>
                {isAdmin && (
                  <button
                    onClick={() => { setRenameValue(location.name); setRenameOpen(true); }}
                    style={linkBtnStyle}
                  >
                    Rename
                  </button>
                )}
              </>
            ) : (
              <div className="pt-loc-rename-row" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  autoFocus
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doRename()}
                  style={{ ...inputStyle, flex: "1 1 160px", minWidth: 0, marginTop: 0 }}
                />
                <button
                  onClick={doRename}
                  disabled={savingOp === "rename"}
                  style={primaryBtnStyle(savingOp !== "rename")}
                >
                  {savingOp === "rename" ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => { setRenameOpen(false); setRenameValue(location.name); }}
                  style={ghostBtnStyle}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div
            style={{
              fontSize: 11, fontWeight: 700,
              color: totalOk ? "var(--pt-green)" : ownerTotal === 0 ? "var(--pt-muted)" : "var(--pt-red)",
              background: totalOk ? "#ecfdf5" : ownerTotal === 0 ? "var(--pt-card-alt)" : "#fef2f2",
              border: `1px solid ${totalOk ? "#a7f3d0" : ownerTotal === 0 ? "var(--pt-border)" : "#fecaca"}`,
              padding: "4px 10px", borderRadius: 10,
            }}
          >
            {ownerTotal}%
          </div>
        </div>

        {/* Existing investors */}
        {investors.length === 0 && (
          <div className="pt-empty" style={{ padding: "30px 10px" }}>
            No partners on this location yet. Add one below.
          </div>
        )}
        {investors.map((inv) => (
          <InvestorRow
            key={inv.investorId}
            inv={inv}
            saving={savingOp === inv.investorId}
            canEdit={isAdmin}
            siblingNames={
              new Set(
                investors
                  .filter((i) => i.investorId !== inv.investorId)
                  .map((i) => i.name.trim().toLowerCase()),
              )
            }
            onUpdate={(patch) => applyUpdate(inv, patch)}
            onDelete={() => setConfirm({ kind: "investor", target: inv })}
          />
        ))}

        {/* Add panel — admin-only */}
        {!isAdmin ? null : !showAddPanel ? (
          <button
            onClick={() => setShowAddPanel(true)}
            className="pt-listbtn"
            style={{
              justifyContent: "center",
              borderStyle: "dashed",
              color: "var(--pt-teal)",
              background: "transparent",
              boxShadow: "none",
            }}
          >
            + Add partner
          </button>
        ) : (
          <div className="pt-card">
            {/* Manager + threshold toggle — applies to whichever add method is used */}
            <div
              style={{
                background: newIsManager ? "var(--pt-mgr-bg)" : "var(--pt-card-alt)",
                border: `1px solid ${newIsManager ? "#ddd6fe" : "var(--pt-border)"}`,
                borderRadius: 10,
                padding: "10px 12px",
                marginBottom: 12,
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={newIsManager}
                  onChange={(e) => setNewIsManager(e.target.checked)}
                  style={{ accentColor: "var(--pt-purple)", width: 16, height: 16, cursor: "pointer" }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--pt-text)" }}>
                    Add as manager
                  </div>
                  <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2, lineHeight: 1.4 }}>
                    Manager cut is deducted from gross profit <i>before</i> the owner split.
                  </div>
                </div>
              </label>
              {newIsManager && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                  <label style={{ fontSize: 12, color: "var(--pt-text-2)", fontWeight: 600, flexShrink: 0 }}>
                    Profit threshold
                  </label>
                  <div style={{ position: "relative", flex: 1 }}>
                    <span style={{
                      position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                      color: "var(--pt-muted)", fontSize: 13, fontWeight: 600,
                    }}>$</span>
                    <input
                      type="number"
                      placeholder="0"
                      value={newThreshold}
                      onChange={(e) => setNewThreshold(e.target.value)}
                      style={{ ...inputStyle, marginTop: 0, paddingLeft: 24, fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="pt-lb">Select existing partner</div>
            {availableNames.length === 0 ? (
              <div style={{ color: "var(--pt-muted)", fontSize: 12, marginBottom: 8 }}>
                All known partners are already on this location.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {availableNames.map((n) => (
                  <button key={n} onClick={() => addPartner(n)} style={pillBtnStyle(colorFor(n))}>
                    {n}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0" }}>
              <div style={{ flex: 1, height: 1, background: "var(--pt-border)" }} />
              <span style={{ color: "var(--pt-muted)", fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: "var(--pt-border)" }} />
            </div>
            <div className="pt-lb">Create new partner</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder="Partner name"
                value={newPartnerName}
                onChange={(e) => setNewPartnerName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addPartner(newPartnerName)}
                style={{ ...inputStyle, flex: 1, marginTop: 0 }}
              />
              <button
                onClick={() => addPartner(newPartnerName)}
                disabled={savingOp === "new" || !newPartnerName.trim()}
                style={primaryBtnStyle(savingOp !== "new" && !!newPartnerName.trim())}
              >
                {savingOp === "new" ? "Adding…" : "Add"}
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: "var(--pt-muted)" }}>
              {newIsManager
                ? `Will be added as a manager${(Number(newThreshold) || 0) > 0 ? ` with $${Number(newThreshold).toLocaleString("en-US")} threshold` : ""}. Percentage starts at 0%.`
                : `Owner will be added at ${remaining}% (remaining share). Adjust after.`}
            </div>
            <button
              onClick={() => {
                setShowAddPanel(false);
                setNewPartnerName("");
                setNewIsManager(false);
                setNewThreshold("");
              }}
              style={{ ...ghostBtnStyle, marginTop: 10 }}
            >
              Close
            </button>
          </div>
        )}

        <SharePeriods location={location} api={api} isAdmin={isAdmin} />

        {/* Archive status note + permanent-delete escape hatch (admin only). */}
        {isAdmin && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--pt-border)" }}>
            {location.archived ? (
              <div style={{ fontSize: 12, color: "var(--pt-muted)", lineHeight: 1.5, marginBottom: 12 }}>
                This location is archived. It's hidden from current and future weeks
                but still appears in past weeks and the annual report, so no historical
                numbers change. Use <b>Unarchive</b> above to bring it back.
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--pt-muted)", lineHeight: 1.5, marginBottom: 12 }}>
                Prefer <b>Archive</b> (top-right) to retire this location — it keeps every
                past report intact. Deleting is permanent and only for locations created
                by mistake.
              </div>
            )}
            <button
              onClick={() => setConfirm({ kind: "delete" })}
              disabled={savingOp === "delete"}
              style={{
                background: "transparent",
                border: "1px solid #fecaca",
                color: "var(--pt-red)",
                padding: "8px 14px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <DeleteIcon style={{ fontSize: 16 }} />
              Delete permanently
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        variant={confirm?.kind === "unarchive" ? "default" : "danger"}
        title={
          confirm?.kind === "archive"
            ? "Archive this location?"
            : confirm?.kind === "unarchive"
            ? "Unarchive this location?"
            : confirm?.kind === "delete"
            ? "Delete this location permanently?"
            : confirm?.kind === "investor"
            ? "Remove partner from location?"
            : ""
        }
        message={
          confirm?.kind === "archive"
            ? `"${location.name}" will be hidden from current and future weeks and moved to the Archived list. Past weeks and the annual report keep it exactly as-is — no historical numbers change. You can unarchive it anytime.`
            : confirm?.kind === "unarchive"
            ? `"${location.name}" will return to the active list and be included in current weeks again.`
            : confirm?.kind === "delete"
            ? `"${location.name}" and every partner assignment on it will be PERMANENTLY removed. This cannot be undone, and any historical profit entries keyed to this location may be affected. Archive instead if you want to keep past reports intact.`
            : confirm?.kind === "investor" && confirm.target
            ? `${confirm.target.name} will be removed from ${location.name}. Their ${Number(confirm.target.percentage) || 0}% share will no longer be counted here.`
            : ""
        }
        confirmLabel={
          confirm?.kind === "archive"
            ? "Archive location"
            : confirm?.kind === "unarchive"
            ? "Unarchive location"
            : confirm?.kind === "delete"
            ? "Delete permanently"
            : "Remove partner"
        }
        busy={
          (confirm?.kind === "archive" && savingOp === "archive") ||
          (confirm?.kind === "unarchive" && savingOp === "unarchive") ||
          (confirm?.kind === "delete" && savingOp === "delete") ||
          (confirm?.kind === "investor" && savingOp === confirm.target?.investorId)
        }
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.kind === "archive") doArchiveLocation();
          else if (confirm?.kind === "unarchive") doUnarchiveLocation();
          else if (confirm?.kind === "delete") doDeleteLocation();
          else if (confirm?.kind === "investor" && confirm.target) removePartner(confirm.target);
        }}
      />
    </>
  );
}

function InvestorRow({ inv, saving, onUpdate, onDelete, canEdit = true, siblingNames }) {
  const [pct, setPct] = useState(String(inv.percentage ?? 0));
  const [name, setName] = useState(inv.name || "");
  const [threshold, setThreshold] = useState(String(inv.profitThreshold ?? 0));
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);

  // Keep local edits in sync if the server response changes the underlying value.
  React.useEffect(() => { setPct(String(inv.percentage ?? 0)); }, [inv.percentage]);
  React.useEffect(() => { setName(inv.name || ""); }, [inv.name]);
  React.useEffect(() => { setThreshold(String(inv.profitThreshold ?? 0)); }, [inv.profitThreshold]);

  const c = colorFor(inv.name);
  const pctNum = Number(pct);
  const trimmedName = name.trim();
  const pctDirty = Number.isFinite(pctNum) && pctNum !== Number(inv.percentage);
  const nameDirty = trimmedName !== (inv.name || "").trim();
  const thrNum = Number(threshold);
  const thrDirty = Number.isFinite(thrNum) && thrNum !== Number(inv.profitThreshold);

  const commitThreshold = () => { if (thrDirty) onUpdate({ profitThreshold: thrNum }); };

  const startEdit = () => {
    setName(inv.name || "");
    setPct(String(inv.percentage ?? 0));
    setEditError(null);
    setEditing(true);
  };
  const cancelEdit = () => {
    setName(inv.name || "");
    setPct(String(inv.percentage ?? 0));
    setEditError(null);
    setEditing(false);
  };
  const saveEdit = () => {
    if (!trimmedName) { setEditError("Name is required"); return; }
    if (siblingNames && siblingNames.has(trimmedName.toLowerCase())) {
      setEditError("Another partner on this location already has that name");
      return;
    }
    if (!Number.isFinite(pctNum) || pctNum < 0) {
      setEditError("Percentage must be a non-negative number");
      return;
    }
    if (!nameDirty && !pctDirty) { setEditing(false); return; }
    const patch = {};
    if (nameDirty) patch.name = trimmedName;
    if (pctDirty) patch.percentage = pctNum;
    setEditError(null);
    onUpdate(patch);
    setEditing(false);
  };

  return (
    <div
      className="pt-card compact"
      style={{ borderLeft: `4px solid ${c}`, padding: 0, overflow: "hidden" }}
    >
      <div
        className={`pt-inv-row ${editing ? "editing" : ""}`}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}
      >
        <div className="pt-inv-avatar" style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${c}15`, border: `1.5px solid ${c}40`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: c, fontSize: 14, fontWeight: 800,
          flexShrink: 0,
        }}>
          {inv.name.charAt(0).toUpperCase()}
        </div>
        <div className="pt-inv-name" style={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                else if (e.key === "Escape") cancelEdit();
              }}
              disabled={saving}
              autoFocus
              placeholder="Partner name"
              style={{
                width: "100%", boxSizing: "border-box",
                background: "var(--pt-card)",
                border: "1px solid var(--pt-border)",
                borderRadius: 8, padding: "6px 10px",
                fontSize: 14, fontWeight: 600,
                color: "var(--pt-text)", outline: "none",
                fontFamily: "inherit",
              }}
            />
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pt-text)", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <span>{inv.name}</span>
                {inv.isManager && <span style={managerBadge}>Manager</span>}
              </div>
              {inv.isManager && (Number(inv.profitThreshold) || 0) > 0 && (
                <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2 }}>
                  Threshold ${Number(inv.profitThreshold).toLocaleString("en-US")}
                </div>
              )}
            </>
          )}
        </div>
        <div className="pt-inv-pct" style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--pt-card-alt)", padding: "4px 6px 4px 10px", borderRadius: 8, flexShrink: 0 }}>
          {editing ? (
            <input
              type="number"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                else if (e.key === "Escape") cancelEdit();
              }}
              disabled={saving}
              style={{
                width: 56, background: "transparent", border: "none",
                color: "var(--pt-text)", fontSize: 15, fontWeight: 700,
                textAlign: "right", outline: "none",
                fontFamily: "inherit", fontVariantNumeric: "tabular-nums",
              }}
            />
          ) : (
            <span style={{ color: "var(--pt-text)", fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums", paddingRight: 2, minWidth: 40, textAlign: "right" }}>
              {Number(inv.percentage) || 0}
            </span>
          )}
          <span style={{ color: "var(--pt-muted)", fontSize: 13, fontWeight: 600 }}>%</span>
        </div>
        {canEdit && !editing && (
          <div className="pt-inv-actions" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={startEdit}
              disabled={saving}
              style={{
                background: "var(--pt-card)",
                color: "var(--pt-text-2)",
                border: "1px solid var(--pt-border)",
                width: 38, height: 38, borderRadius: 10,
                cursor: "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
              title="Edit partner"
              aria-label="Edit partner"
            >
              <EditIcon style={{ fontSize: 20 }} />
            </button>
            <button
              onClick={onDelete}
              disabled={saving}
              style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                width: 38, height: 38, borderRadius: 10, cursor: "pointer",
                color: "var(--pt-red)", display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
              title="Remove partner"
            >
              <DeleteIcon style={{ fontSize: 20 }} />
            </button>
          </div>
        )}
        {canEdit && editing && (
          <div className="pt-inv-actions" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={saveEdit}
              disabled={saving}
              style={primaryBtnStyle(!saving)}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              style={ghostBtnStyle}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {editing && editError && (
        <div
          style={{
            borderTop: "1px solid var(--pt-border)",
            background: "#fef2f2",
            color: "var(--pt-red)",
            padding: "8px 14px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {editError}
        </div>
      )}

      {canEdit && editing && (
        <div style={{
          borderTop: "1px solid var(--pt-border)",
          background: "var(--pt-card-alt)",
          padding: "12px 14px",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!inv.isManager}
              onChange={(e) => onUpdate({ isManager: e.target.checked })}
              disabled={saving}
              style={{ accentColor: "var(--pt-purple)", width: 16, height: 16, cursor: "pointer" }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--pt-text)" }}>
                Manager
              </div>
              <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2, lineHeight: 1.4 }}>
                Manager cut is deducted from gross profit before the owner split. Owner % resets to 0 when enabled.
              </div>
            </div>
          </label>
          {inv.isManager && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 12, color: "var(--pt-text-2)", fontWeight: 600, flexShrink: 0 }}>
                Profit threshold
              </label>
              <div style={{ position: "relative", flex: 1 }}>
                <span style={{
                  position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                  color: "var(--pt-muted)", fontSize: 13, fontWeight: 600,
                }}>$</span>
                <input
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  onBlur={commitThreshold}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  disabled={saving}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: "var(--pt-card)",
                    border: "1px solid var(--pt-border)",
                    borderRadius: 8,
                    padding: "8px 10px 8px 24px",
                    fontSize: 13, color: "var(--pt-text)",
                    outline: "none", fontFamily: "inherit",
                    fontVariantNumeric: "tabular-nums",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Share periods — effective-dated snapshots of the layout
   ══════════════════════════════════════════════════════════ */

// A styled date picker: shows the date as "Oct 20, 2026 / Tuesday" over a
// hidden native <input type="date">, whose picker opens on click.
function DateField({ value, onChange, min, max, invalid, placeholder = "Pick a date" }) {
  const inputRef = React.useRef(null);
  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    try { el.showPicker(); } catch (e) { el.focus(); }
  };
  const [y, m, d] = (value || "").split("-").map(Number);
  const label = m ? `${MONTHS_SHORT[m - 1]} ${d}, ${y}` : placeholder;
  const weekday = m
    ? new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long" })
    : "Click to choose";
  return (
    <div
      className={`pt-datefield${value ? "" : " empty"}${invalid ? " invalid" : ""}`}
      onClick={openPicker}
    >
      <span className="pt-datefield-icon"><CalendarTodayIcon style={{ fontSize: 17 }} /></span>
      <span className="pt-datefield-text">
        <span className="pt-datefield-main">{label}</span>
        <span className="pt-datefield-sub">{weekday}</span>
      </span>
      <span className="pt-datefield-chev"><ExpandMoreIcon style={{ fontSize: 20 }} /></span>
      <input
        ref={inputRef}
        type="date"
        className="pt-datefield-input"
        value={value || ""}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// Effective-dated share history. Each period stores the percentages that apply
// from its start date to its end date (or "present" — open-ended, until a later
// open-ended period starts), so historical weekly reports use the shares that
// were in force then instead of recomputing from the live (current)
// percentages. You type each period's split directly here.
function SharePeriods({ location, api, isAdmin }) {
  const locationId = location.locationId;
  const roster = location.investors || [];

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // "save" | configId being deleted
  // form === null when closed; otherwise the period being added/edited.
  const [form, setForm] = useState(null);

  const now = new Date();
  const pad2 = (n) => String(n).padStart(2, "0");
  const todayISO = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

  const load = React.useCallback(() => {
    setLoading(true);
    api.listShareConfigs(
      { locationId },
      (res) => {
        setLoading(false);
        if (!res?.success) { setError(res?.message || "Failed to load periods"); return; }
        setConfigs(res.configs || []);
        setError(null);
      },
      (err) => { setLoading(false); setError(typeof err === "string" ? err : "Failed to load periods"); },
    );
  }, [api, locationId]);

  React.useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    const [y, m, d] = (iso || "").split("-").map(Number);
    return m ? `${MONTHS_SHORT[m - 1]} ${d}, ${y}` : iso;
  };
  // The day BEFORE a given ISO date (an open-ended period runs until the day
  // before the next open-ended one starts).
  const dayBefore = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d - 1));
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
  };

  // Ascending by effectiveFrom so an open-ended period's end can be derived
  // from the next open-ended one. Bounded periods only override it within their
  // own range, so they don't end it. Server returns newest-first; copy first.
  const ascending = [...configs].sort((a, b) =>
    a.effectiveFrom < b.effectiveFrom ? -1 : a.effectiveFrom > b.effectiveFrom ? 1 : 0,
  );
  const rangeLabel = (cfg) => {
    if (cfg.effectiveTo) return `${fmtDate(cfg.effectiveFrom)} → ${fmtDate(cfg.effectiveTo)}`;
    const next = ascending.find((c) => c.effectiveFrom > cfg.effectiveFrom && !c.effectiveTo);
    const end = next ? fmtDate(dayBefore(next.effectiveFrom)) : "present";
    return `${fmtDate(cfg.effectiveFrom)} → ${end}`;
  };

  // Seed an editable draft: from the live roster for a new period, or from the
  // saved shares when editing an existing one.
  const seedShares = (source) =>
    source.map((s) => ({
      investorId: s.investorId,
      name: s.name,
      isManager: !!s.isManager,
      profitThreshold: Number(s.profitThreshold) || 0,
      percentage: Number(s.percentage) || 0,
    }));

  const openAdd = () => {
    setError(null);
    setForm({
      configId: null,
      from: todayISO,
      to: "",
      present: true,
      shares: seedShares(roster),
    });
  };
  const openEdit = (cfg) => {
    setError(null);
    setForm({
      configId: cfg.configId,
      from: cfg.effectiveFrom,
      to: cfg.effectiveTo || "",
      present: !cfg.effectiveTo,
      shares: seedShares(cfg.shares || []),
    });
  };

  const setFormShare = (idx, patch) =>
    setForm((f) => ({
      ...f,
      shares: f.shares.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));

  const draftOwnerTotal = form
    ? form.shares.filter((s) => !s.isManager).reduce((sum, s) => sum + (Number(s.percentage) || 0), 0)
    : 0;
  const draftOk = Math.abs(draftOwnerTotal - 100) < 0.01;
  const draftEffectiveFrom = form ? form.from : null;
  const draftEffectiveTo = form && !form.present ? form.to : null;
  // Inputs give 'yyyy-MM-dd', so string comparison orders dates correctly.
  const draftDateError = !form
    ? null
    : !form.from
    ? "Pick a start date."
    : !form.present && !form.to
    ? "Pick an end date, or tick Present."
    : !form.present && form.to < form.from
    ? "End date can't be before the start date."
    : null;
  const draftCollision = form
    ? configs.some((c) => c.effectiveFrom === draftEffectiveFrom && c.configId !== form.configId)
    : false;

  const saveDraft = () => {
    if (!form) return;
    const shares = form.shares.map((s) => ({
      investorId: s.investorId,
      name: s.name,
      percentage: Number(s.percentage) || 0,
      profitThreshold: Number(s.profitThreshold) || 0,
      isManager: !!s.isManager,
    }));
    setBusy("save");
    setError(null);
    api.saveShareConfig(
      {
        locationId,
        configId: form.configId,
        effectiveFrom: draftEffectiveFrom,
        effectiveTo: draftEffectiveTo,
        shares,
      },
      (res) => {
        setBusy(null);
        if (!res?.success) { setError(res?.message || "Failed to save period"); return; }
        setForm(null);
        load();
      },
      (err) => { setBusy(null); setError(typeof err === "string" ? err : "Failed to save period"); },
    );
  };

  const deletePeriod = (cfg) => {
    setBusy(cfg.configId);
    setError(null);
    api.deleteShareConfig(
      { configId: cfg.configId },
      (res) => {
        setBusy(null);
        if (!res?.success) { setError(res?.message || "Failed to delete period"); return; }
        load();
      },
      (err) => { setBusy(null); setError(typeof err === "string" ? err : "Failed to delete period"); },
    );
  };

  return (
    <div className="pt-card" style={{ marginTop: 16 }}>
      <div className="pt-lb">Share periods</div>
      <div style={{ fontSize: 11, color: "var(--pt-muted)", lineHeight: 1.5, marginBottom: 12 }}>
        Set the split for each date range. A period runs from its start date to
        its end date, or — when set to Present — until the next open-ended period
        starts. Where periods overlap, the one that started later wins. Weekly
        reports use the period in force on the week's start date, so editing
        shares no longer rewrites past payouts. Weeks not covered by any period
        use the live layout above.
      </div>

      {error && <div className="pt-error" style={{ marginBottom: 10 }}>{error}</div>}

      {loading ? (
        <div style={{ color: "var(--pt-muted)", fontSize: 12 }}>Loading periods…</div>
      ) : configs.length === 0 ? (
        <div className="pt-empty" style={{ padding: "16px 10px" }}>
          No share periods yet. Add one for each range whose split differs.
        </div>
      ) : (
        configs.map((cfg) => {
          const owners = (cfg.shares || []).filter((s) => !s.isManager);
          const managers = (cfg.shares || []).filter((s) => s.isManager);
          return (
            <div
              key={cfg.configId}
              className="pt-card compact"
              style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--pt-text)" }}>
                  {rangeLabel(cfg)}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                  {owners.map((s) => (
                    <span key={s.investorId || s.name} style={partnerPill(colorFor(s.name))}>
                      {s.name} {Number(s.percentage) || 0}%
                    </span>
                  ))}
                  {managers.map((s) => (
                    <span key={s.investorId || s.name} style={partnerPill("#7c3aed")}>
                      {s.name} · mgr
                      {(Number(s.profitThreshold) || 0) > 0
                        ? ` (${Number(s.percentage) || 0}% > $${Number(s.profitThreshold).toLocaleString("en-US")})`
                        : ` ${Number(s.percentage) || 0}%`}
                    </span>
                  ))}
                </div>
              </div>
              {isAdmin && (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => openEdit(cfg)}
                    disabled={busy === cfg.configId}
                    style={{
                      background: "var(--pt-card)", border: "1px solid var(--pt-border)",
                      width: 34, height: 34, borderRadius: 9, cursor: "pointer",
                      color: "var(--pt-text-2)", display: "flex", alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Edit period"
                    aria-label="Edit period"
                  >
                    <EditIcon style={{ fontSize: 18 }} />
                  </button>
                  <button
                    onClick={() => deletePeriod(cfg)}
                    disabled={busy === cfg.configId}
                    style={{
                      background: "#fef2f2", border: "1px solid #fecaca",
                      width: 34, height: 34, borderRadius: 9, cursor: "pointer",
                      color: "var(--pt-red)", display: "flex", alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Delete period"
                    aria-label="Delete period"
                  >
                    <DeleteIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}

      {isAdmin && !form && (
        <button
          onClick={openAdd}
          className="pt-listbtn"
          style={{
            justifyContent: "center", borderStyle: "dashed",
            color: "var(--pt-teal)", background: "transparent",
            boxShadow: "none", marginTop: 8,
          }}
        >
          + Add a period
        </button>
      )}

      {isAdmin && form && (
        <div style={{ marginTop: 10, borderTop: "1px solid var(--pt-border)", paddingTop: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
              <div className="pt-range-head"><div className="pt-lb">From</div></div>
              <DateField
                value={form.from}
                max={form.present || !form.to ? undefined : form.to}
                onChange={(v) => setForm((f) => ({ ...f, from: v }))}
              />
            </div>
            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
              <div className="pt-range-head">
                <div className="pt-lb">To</div>
                <div className="pt-seg" role="group" aria-label="Period end">
                  <button
                    type="button"
                    className={form.present ? "" : "on"}
                    onClick={() => setForm((f) => ({ ...f, present: false }))}
                  >
                    End date
                  </button>
                  <button
                    type="button"
                    className={form.present ? "on" : ""}
                    onClick={() => setForm((f) => ({ ...f, present: true }))}
                  >
                    Present
                  </button>
                </div>
              </div>
              {form.present ? (
                <div className="pt-datefield present">
                  <span className="pt-datefield-icon"><EventAvailableIcon style={{ fontSize: 18 }} /></span>
                  <span className="pt-datefield-text">
                    <span className="pt-datefield-main"><span className="pt-live-dot" />Present</span>
                    <span className="pt-datefield-sub">Ongoing — no end date</span>
                  </span>
                </div>
              ) : (
                <DateField
                  value={form.to}
                  min={form.from || undefined}
                  invalid={!!form.to && !!form.from && form.to < form.from}
                  placeholder="Pick an end date"
                  onChange={(v) => setForm((f) => ({ ...f, to: v }))}
                />
              )}
            </div>
          </div>

          <div className="pt-lb" style={{ marginTop: 14 }}>Split for this period</div>
          {form.shares.length === 0 ? (
            <div style={{ color: "var(--pt-muted)", fontSize: 12, marginTop: 6 }}>
              No partners on this location yet — add them in the layout above first.
            </div>
          ) : (
            form.shares.map((s, idx) => {
              const c = colorFor(s.name);
              return (
                <div
                  key={s.investorId || s.name}
                  style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: `${c}15`, border: `1.5px solid ${c}40`, color: c,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 800,
                  }}>
                    {(s.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: "var(--pt-text)" }}>
                    {s.name}
                    {s.isManager && <span style={managerBadge}>Manager</span>}
                  </div>
                  {s.isManager && (
                    <div style={{ position: "relative", width: 92 }}>
                      <span style={{
                        position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                        color: "var(--pt-muted)", fontSize: 12, fontWeight: 600,
                      }}>$</span>
                      <input
                        type="number"
                        value={s.profitThreshold}
                        onChange={(e) => setFormShare(idx, { profitThreshold: e.target.value })}
                        title="Profit threshold"
                        style={{ ...inputStyle, marginTop: 0, paddingLeft: 18, width: "100%", boxSizing: "border-box" }}
                      />
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <input
                      type="number"
                      value={s.percentage}
                      onChange={(e) => setFormShare(idx, { percentage: e.target.value })}
                      style={{ ...inputStyle, marginTop: 0, width: 64, textAlign: "right" }}
                    />
                    <span style={{ color: "var(--pt-muted)", fontSize: 13, fontWeight: 600 }}>%</span>
                  </div>
                </div>
              );
            })
          )}

          <div style={{
            fontSize: 11, marginTop: 10,
            color: draftDateError
              ? "var(--pt-red)"
              : draftCollision ? "var(--pt-amber-txt)" : draftOk ? "var(--pt-muted)" : "var(--pt-red)",
          }}>
            {draftDateError
              ? draftDateError
              : draftCollision
              ? `A period already starts ${fmtDate(draftEffectiveFrom)} — saving overwrites it.`
              : draftOk
              ? `Owner percentages total ${draftOwnerTotal}%.`
              : `Owner percentages total ${draftOwnerTotal}% — they must add up to 100%.`}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              onClick={saveDraft}
              disabled={busy === "save" || form.shares.length === 0 || !draftOk || !!draftDateError}
              style={primaryBtnStyle(busy !== "save" && form.shares.length > 0 && draftOk && !draftDateError)}
            >
              {busy === "save" ? "Saving…" : "Save period"}
            </button>
            <button onClick={() => setForm(null)} style={ghostBtnStyle}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Partners list — grouped by name across locations
   ══════════════════════════════════════════════════════════ */

export function PartnersList({ nav, locations }) {
  const partners = useMemo(() => {
    const map = new Map();
    // Partners aggregation has no profit figures to weigh, so archived
    // locations are simply left out. A partner who is only on archived
    // locations drops off this active-roster view.
    locations.filter((l) => !l.archived).forEach((l) => {
      (l.investors || []).forEach((inv) => {
        if (!map.has(inv.name)) {
          map.set(inv.name, {
            name: inv.name,
            locations: [],
            isManager: inv.isManager,
            totalPct: 0,
          });
        }
        const p = map.get(inv.name);
        p.locations.push({ name: l.name, pct: Number(inv.percentage) || 0 });
        p.totalPct += Number(inv.percentage) || 0;
        if (inv.isManager) p.isManager = true;
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [locations]);

  return (
    <>
      <PTToolbar title="Partners" onBack={() => nav("settings")} />
      <div className="pt-content">
        {partners.length === 0 && <div className="pt-empty">No partners yet.</div>}
        {partners.map((p) => {
          const c = colorFor(p.name);
          return (
            <div key={p.name} className="pt-card compact" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: `${c}15`, border: `1.5px solid ${c}40`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: c, fontSize: 16, fontWeight: 800, flexShrink: 0,
              }}>
                {p.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {p.name}
                  {p.isManager && <span style={managerBadge}>Manager</span>}
                </div>
                <div style={{ color: "var(--pt-muted)", fontSize: 11, marginTop: 2 }}>
                  {p.locations.length} location{p.locations.length !== 1 ? "s" : ""}
                </div>
                {p.locations.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                    {p.locations.map((l) => (
                      <span key={l.name} style={partnerPill(c)}>
                        {l.name} · {l.pct}%
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ─── Inline style helpers (small enough to keep near usage) ── */

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: "var(--pt-card)",
  border: "1px solid var(--pt-border)",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  color: "var(--pt-text)",
  outline: "none",
  marginTop: 8,
  fontFamily: "inherit",
};

const primaryBtnStyle = (active) => ({
  background: active ? "var(--pt-teal)" : "var(--pt-border)",
  border: "none",
  color: active ? "#fff" : "var(--pt-muted)",
  padding: "10px 16px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: active ? "pointer" : "default",
  whiteSpace: "nowrap",
});
const ghostBtnStyle = {
  background: "var(--pt-card)",
  border: "1px solid var(--pt-border)",
  color: "var(--pt-text-2)",
  padding: "10px 14px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};
const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "var(--pt-teal)",
  fontSize: 12,
  fontWeight: 600,
  padding: 0,
  marginTop: 2,
  cursor: "pointer",
  fontFamily: "inherit",
};
const partnerPill = (c) => ({
  background: `${c}12`,
  color: c,
  border: `1px solid ${c}30`,
  padding: "2px 8px",
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 600,
});
const pillBtnStyle = (c) => ({
  background: `${c}10`,
  color: c,
  border: `1px solid ${c}30`,
  padding: "6px 12px",
  borderRadius: 10,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
});
const managerBadge = {
  background: "var(--pt-mgr-bg)",
  color: "var(--pt-purple)",
  border: "1px solid #ddd6fe",
  fontSize: 9,
  fontWeight: 700,
  padding: "2px 7px",
  borderRadius: 4,
  marginLeft: 8,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  verticalAlign: "middle",
};
const archivedBadge = {
  background: "var(--pt-card-alt)",
  color: "var(--pt-muted)",
  border: "1px solid var(--pt-border)",
  fontSize: 9,
  fontWeight: 700,
  padding: "2px 7px",
  borderRadius: 4,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  whiteSpace: "nowrap",
};

// keep exported even if not used elsewhere — avoids orphan-import lint
export const _icons = { SaveIcon };
